import type React from 'react';
import { useEffect } from 'react';
import { Box, Page } from '@zillow/constellation-10';
import { PageFooter } from '@zillow/page-frame';
import BlockedUserModal from '@zg-rentals/blocked-user-modal';
import { CustomizedBlockedUserModal } from '@zg-rentals/satellite-components';
import type { SystemStyleObject } from '@/styled-system/types';
import StringConstants from '../../constants/StringConstants';
import Link from 'next/link';
import { AppLayoutContainer } from '@zg-rentals/zrm-header-navigation';
import useFeatureFlagsHelper from '../../hooks/useFeatureFlagsHelper';
import useModal from '../../hooks/useModal';
import { ModalType } from '../../../src/types/ModalType';
import { useAuth } from '@zg-rentals/auth-comet';
import { AppDownloadUpsellBanner } from '@zg-rentals/app-download-upsell-banner';
import { onPageFrameLinkClick } from '@zg-rentals/datagrail';

export default function Layout({
  children,
  showFooter = true,
  showHeader = true,
  mainContentStyles = {},
  pageRootStyles = {},
  appLayoutContainerStyles = {},
  isPageFluid = false,
}: {
  children: React.ReactNode;
  showFooter?: boolean;
  showHeader?: boolean;
  mainContentStyles?: SystemStyleObject;
  pageRootStyles?: SystemStyleObject;
  appLayoutContainerStyles?: SystemStyleObject;
  isPageFluid?: boolean;
}) {
  const { canAccessBlockedUserModalFromRJSModule } = useFeatureFlagsHelper();
  const { activeModal, setActiveModal } = useModal();

  const hostName = typeof window !== 'undefined' && window !== null ? window.location.host : 'www.zillow.com';
  const pathname = typeof window !== 'undefined' && window !== null ? window.location.href : '';

  const { user } = useAuth();
  const { isEnterpriseCustomer, isUserBlocked, restrictions } = user ?? {};
  const { isAllowed } = restrictions ?? {};

  const shouldDisplayBlockedUserModalFromModule =
    isAllowed === false && canAccessBlockedUserModalFromRJSModule && isUserBlocked;
  const shouldDisplayCustomizedBlockedUserModal = !canAccessBlockedUserModalFromRJSModule && isUserBlocked;

  useEffect(() => {
    if (shouldDisplayCustomizedBlockedUserModal) {
      setActiveModal(ModalType.CustomizedBlockedModal);
    } else if (shouldDisplayBlockedUserModalFromModule) {
      setActiveModal(ModalType.BlockedModalFromModule);
    }
  }, [shouldDisplayCustomizedBlockedUserModal, shouldDisplayBlockedUserModalFromModule, activeModal]);

  return (
    <Page.Root fluid={isPageFluid} css={{ backgroundColor: 'bg.neutral', paddingBottom: '0', ...pageRootStyles }}>
      <AppDownloadUpsellBanner />

      {/* use the blocked user modal from the blocked-user-modal module, default to use our version if the feature flag value is false */}
      {activeModal === ModalType.BlockedModalFromModule && user != null && <BlockedUserModal user={user} />}
      {activeModal === ModalType.CustomizedBlockedModal && <CustomizedBlockedUserModal brand="zillow" />}

      {showHeader && user != null && (
        <Box asChild css={appLayoutContainerStyles}>
          <AppLayoutContainer
            altLogo={!isEnterpriseCustomer}
            brand={StringConstants.UNFORMATTED_BRAND_NAMES.zillow}
            userModel={user}
            useRelativePaths={false}
            reactRouterLink={Link}
            host={hostName}
            pathname={pathname}
            displayMainMyProperties={true}
          >
            <Box asChild css={mainContentStyles}>
              <main>{children}</main>
            </Box>
            {showFooter && <PageFooter onLinkClick={onPageFrameLinkClick} />}
          </AppLayoutContainer>
        </Box>
      )}
    </Page.Root>
  );
}
